import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button, HStack,
  Icon, IconButton,
  Stack,
  Text, Tooltip,
  useBreakpointValue
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FullWidthBlock, PageTitle } from '../layouts';
import React from 'react';
import styled from '@emotion/styled';
import { breakpoints } from '@payler/ui-theme';
import FasterPayoutsTable from '../tables/FasterPayoutsTable/FasterPayoutsTable';
import { Card, CardContent, CardHead, CardTitle } from '@payler/ui-components';
import { FasterPayoutsFiltersForm } from '../forms/FasterPayoutsFilters/FasterPayoutsFiltersForm';
import {
  FasterPayoutsFiltersModal,
  useShowFasterPayoutsModal,
} from '../modals/FasterPayoutsFiltersModal';
import { Filter2Icon, PlusCircleIcon16 } from '@payler/ui-icons';
import { Helmet } from 'react-helmet-async';
import { useFasterPayoutsBalancesQuery, useSelectedContract } from '../hooks/contracts';
import { useUserPermissions } from '../hooks/user/users';
import { NewReportModalSBPPayout, useShowNewReportModal } from '../modals/NewReportModalSBP';
import { BalancesStrip } from '../components/Balances/BalancesStrip';

const Wrap = styled.div`
  @media (min-width: ${breakpoints.lg}) {
    display: grid;
    grid-template-columns: 1fr 320px;
    grid-template-areas: 'table filters';
    grid-column-gap: 16px;
    align-items: flex-start;
  }
`;

export const FasterPayoutsPage = () => {
  const { t } = useTranslation(['fasterPayouts', 'titles']);
  return (
    <>
      <Helmet title={t('titles:fasterPayouts')} />
      <FasterPayoutsPageHeader />
      <MobileFiltersButton />
      <BalancesSection />
      <Wrap>
        <FullWidthBlock
          bg="secondary.500"
          borderRadius={{ base: 0, sm: 1.5 }}
          border="0.5px solid"
          borderColor="primary.100"
          gridArea="table"
        >
          <Card>
            <CardHead>
              <CardTitle>{t('fasterPayouts:previousPayments')}</CardTitle>
            </CardHead>
            <CardContent p={0}>
              <FasterPayoutsTable />
            </CardContent>
          </Card>
        </FullWidthBlock>
        <FasterPayoutsFiltersDesktop />
      </Wrap>
      <FasterPayoutsFiltersModal />
      <NewReportModalSBPPayout />
    </>
  );
};

const MobileFiltersButton = () => {
  const showModal = useShowFasterPayoutsModal();
  const { t } = useTranslation(['filters']);
  const visible = useBreakpointValue({ base: true, lg: false });
  if (!visible) return null;
  return (
    <Button
      onClick={showModal}
      mb={2}
      w="100%"
      variant="secondary"
      leftIcon={<Icon as={Filter2Icon} color="primary.350" />}
    >
      {t('filters:title')}
    </Button>
  );
};

const BalancesSection = () => {
  const contract = useSelectedContract();
  const { data: balances } = useFasterPayoutsBalancesQuery(contract?.id);
  return <BalancesStrip balances={balances} />;
};

const FasterPayoutsFiltersDesktop = () => {
  const visible = useBreakpointValue({ base: false, lg: true });
  if (!visible) return null;
  return (
    <Box
      gridArea="filters"
      bg="secondary.500"
      border="0.5px solid"
      borderColor="primary.100"
      borderRadius={1.5}
    >
      <FasterPayoutsFiltersForm />
    </Box>
  );
};

const FasterPayoutsPageHeader = () => {
  const showNewReportModal = useShowNewReportModal();
  const { canCreateReports } = useUserPermissions();
  const mobile = useBreakpointValue({ base: true, sm: false });
  const { t } = useTranslation(['common', 'fasterPayouts', 'common', 'titles', 'reports']);
  const contract = useSelectedContract();
  return (
    <Stack
      alignItems={{ base: 'flex-start', lg: 'flex-end' }}
      justifyContent="space-between"
      mb={{ base: 2, sm: 5 }}
      direction={{
        base: 'column',
        lg: 'row',
      }}
      spacing={{ base: 2, md: 3, lg: 2 }}
    >
      <div style={{ width: '100%' }}>
        <Breadcrumb separator={<>&mdash;</>}>
          <BreadcrumbItem>
            <BreadcrumbLink as={Link} to="/">
              {t('common:home')}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink as={Text}>{contract?.name}</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink as={Text}>
              {t('fasterPayouts:title')}
            </BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>
        <HStack
          alignItems="flex-end"
          justifyContent="space-between"
          mb={{ base: 2, sm: 5 }}
        >
          <PageTitle mb={0}>{t('fasterPayouts:title')}</PageTitle>
          {mobile ? (
            <Tooltip
              label={!canCreateReports ? t('common:noAccess') : ''}
              shouldWrapChildren
            >
              <IconButton
                disabled={!canCreateReports}
                aria-label={t('reports:saveReport')}
                icon={<PlusCircleIcon16 />}
                p={3}
                py={2}
                onClick={showNewReportModal}
              />
            </Tooltip>
          ) : (
            <Tooltip
              label={!canCreateReports ? t('common:noAccess') : ''}
              shouldWrapChildren
            >
              <Button disabled={!canCreateReports} onClick={showNewReportModal}>
                {t('reports:saveReport')}
              </Button>
            </Tooltip>
          )}
        </HStack>
      </div>
    </Stack>
  );
};
